<template>
  <div class="ishikawa-branch" :style="branchStyle">
    <canvas ref="branchCanvas" />
    <div v-if="mounted" class="ishikawa-branch__content-wrapper" :style="contentWrapperStyle">
      <div
        v-if="!isBottom"
        ref="branchHead"
        class="branch-head"
        :style="headStyle"
      >
        {{ decodeHTML(branch.name) }}
      </div>
      <br><!-- When 0 causes, head is inline with the button -->
      <IshikawaCause
        v-for="(cause, index) in branch.wCauses"
        :ref="`cause${cause.id}`"
        :key="`cause${cause.id}-${causeKeyModifier}`"
        :problem-id="problemId"
        :cause="cause"
        :container="container"
        :is-bottom="isBottom"
        :index-from-center="(isBottom ? index : branch.wCauses.length - index) + 1"
        :branch-element="$el"
        :sheet-selection="sheetSelection"
        @margin-set="causeMarginSet(cause, $event)"
        @size-update="causeSizeUpdate"
        @showActionsPerCauseModal="$emit('showActionsPerCauseModal', $event)"
      />
      <button
        ref="addBtn"
        class="generic-border generic-border--text-btn generic-border--gray-border"
        :style="addButtonStyle"
        @click="addCause"
      >
        <div class="generic-border__left">
          <Icon class="generic-border__left-blue-icon">{{ addExtraBold }}</Icon>
          <span>{{ $t('message.ADD_A_CAUSE') }}</span>
        </div>
        <Icon class="generic-border__right-arrow">{{ arrowDownLine }}</Icon>
      </button>
      <br><!-- When 0 causes, head is inline with the button -->
      <div
        v-if="isBottom"
        ref="branchHead"
        class="branch-head"
        :style="headStyle"
      >
        {{ decodeHTML(branch.name) }}
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Branch of an Ishikawa diagram
 *
 * @vue-prop {Number} problemId - Used for XHR
 * @vue-prop {Object} branch - Branch object
 * @vue-prop {Boolean} [isBottom] - This branch is on the bottom section
 * @vue-prop {Boolean} [disabled] - Cannot be edited
 * @vue-prop {Boolean} [sheetSelection] - Wether the sheet selection is on or off
 *
 * @vue-event {} size-update - Forwarded from IshikawaCause
 */

import $ from 'jquery';
// Vuex
import { ADD_CAUSE, EDIT_CAUSE } from '@/store/modules/problem/action-types';
// Components
import Icon from '@/components/icon.vue';
import IshikawaCause from '@/components/ishikawa-cause.vue';
// Utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';
import { addExtraBold, arrowDownLine } from '@/assets/icons';
import {
  preventPastingStyles,
  calert,
  decodeHTML,
  treeAsArray,
} from '@/utils/main';
import { branchHeadHeight, branchAngleTan, getIshikawaCauseWidth } from '@/utils/ishikawa-utils';
import { SColors } from '@/utils/constants';

export default {

  name: 'IshikawaBranch',

  components: {
    Icon,
    IshikawaCause,
  },

  mixins: [mixinSocketHandler],

  props: {
    problemId: {
      type: Number,
      required: true,
    },
    branch: {
      type: Object,
      required: true,
    },
    container: {
      type: HTMLElement,
      default: null,
    },
    isBottom: Boolean,
    disabled: Boolean,
    sheetSelection: Boolean,
  },

  data() {
    return {
      addExtraBold,
      arrowDownLine,
      mounted: false,
      // DOM elt margin per direct cause child
      causesMargins: {},
      branchHeight: 0,
      addBtnMargin: 0,
      causeKeyModifier: 0,
    };
  },

  computed: {
    /** Left padding of $el */
    leftPadding() {
      return Object.keys(this.causesMargins)
        .reduce((max, k) => (max > this.causesMargins[k] ? max : this.causesMargins[k]), 30);
    },
    /** Style applied to $el */
    branchStyle() {
      return {
        alignSelf: this.isBottom ? 'flex-start' : 'flex-end',
        alignItems: this.isBottom ? 'flex-start' : 'flex-end',
        'padding-left': `${this.leftPadding}px`,
      };
    },
    /** Style applied to the 'add cause' button */
    addButtonStyle() {
      const width = getIshikawaCauseWidth(this.$store.state);
      return {
        width: `${this.branch.wCauses.length ? width : width - 40}px`,
        marginRight: `${this.addBtnMargin}px`,
        marginLeft: `${-this.addBtnMargin + 40}px`,
      };
    },
    /** Style applied to the 'add cause' button */
    headStyle() {
      const rightMargin = (this.branchHeight - branchHeadHeight) * branchAngleTan;
      return {
        marginRight: `${rightMargin}px`,
        transform: 'translateX(25%)',
        marginTop: this.isBottom ? '10px' : '0px',
        marginBottom: this.isBottom ? '0px' : '10px',
        height: `${branchHeadHeight}px`,
      };
    },
    /** Style applied to the content wrapper (padding depending on isBottom) */
    contentWrapperStyle() {
      return {
        paddingTop: this.isBottom ? '10px' : '0px',
        paddingBottom: this.isBottom ? '0px' : '5px',
      };
    },
    nTotalCauses() {
      return treeAsArray(this.branch.wCauses, 'wCauses').length;
    },
  },

  watch: {
    nTotalCauses(nv, ov) {
      if (nv === 0 && ov > 0) {
        this.calculateAddBtnMargin();
      }
    },
  },

  mounted() {
    this.mounted = true;
    this.$nextTick(this.setupDroppable);
    this.$nextTick(this.setBranchHeight);
    this.$nextTick(this.calculateAddBtnMargin);

    $(window).on(`resizeEnd.${this.uuid}`, () => {
      this.causeKeyModifier++;
      this.calculateAddBtnMargin();
    });
  },

  methods: {
    decodeHTML,
    /** Child margin has been calculated */
    causeMarginSet(cause, margin) {
      this.$set(this.causesMargins, `${cause.id}`, margin);
      this.$nextTick(this.setBranchHeight);
      this.$emit('margin-set');
    },
    /** Calculate this.branchHeight */
    setBranchHeight() {
      const rect = this.$el.getBoundingClientRect();
      this.branchHeight = rect.bottom - rect.top;
      this.calculateAddBtnMargin();
    },
    /** Allow dropping causes over the branch head */
    setupDroppable() {
      const droppableOptions = {
        accept: '.ishikawa-cause',
        tolerance: 'pointer',
        drop: (e, ui) => {
          const helper = ui.helper.length ? ui.helper[0] : ui.helper;
          // Droppable has been handled elsewhere
          if (helper.dataset.handled === '1') {
            return;
          }
          const { id } = helper.dataset;
          this.editCause({ cause: 0, ishikawaBranch: this.branch.id }, id);
        },
      };
      $(this.$refs.branchHead).droppable(droppableOptions);
      $(this.$refs.addBtn).droppable(droppableOptions);
    },
    /** The size of a cause has been updated */
    causeSizeUpdate(evt) {
      this.$nextTick(() => {
        this.setBranchHeight();
        // Update margin and canvas of the causes of that branch
        this.branch.wCauses.forEach((cause) => {
          if (this.$refs[`cause${cause.id}`]) {
            this.$refs[`cause${cause.id}`][0].init();
          }
        });

        this.$emit('size-update', evt);
        this.calculateAddBtnMargin();
      });
    },
    /** Reset the right margin of this component and draw the canvas */
    calculateAddBtnMargin() {
      const rect = this.$refs.addBtn.getBoundingClientRect();
      const parentRect = this.$el.getBoundingClientRect();
      if (this.nTotalCauses === 0) {
        this.addBtnMargin = 0;
      } else {
        this.addBtnMargin = this.isBottom
          ? branchAngleTan * (rect.bottom - parentRect.top)
          : branchAngleTan * (parentRect.bottom - rect.top);
      }
      this.$nextTick(this.drawAddBtnCanvas);
    },
    drawAddBtnCanvas() {
      const { branchCanvas } = this.$refs;
      if (!branchCanvas) {
        return;
      }
      const rect = this.$refs.addBtn.getBoundingClientRect();
      const parentRect = this.$el.getBoundingClientRect();
      const { offsetWidth, offsetHeight } = this.$el;
      branchCanvas.width = offsetWidth;
      branchCanvas.height = offsetHeight;
      const ctx = branchCanvas.getContext('2d');
      const yHalfHeightCoordinate = (rect.top - parentRect.top) + rect.height / 2;
      ctx.clearRect(0, 0, offsetWidth, offsetHeight);
      ctx.beginPath();
      ctx.moveTo(rect.right - parentRect.left, yHalfHeightCoordinate);
      const rightShift = branchAngleTan
        * (this.isBottom ? yHalfHeightCoordinate : parentRect.height - yHalfHeightCoordinate);
      ctx.lineTo(parentRect.width - rightShift, yHalfHeightCoordinate);
      ctx.lineWidth = 2;
      ctx.strokeStyle = SColors.mainTextColor;
      ctx.stroke();
    },
    /** Add a cause to the current branch */
    addCause() {
      const oldIds = this.branch.wCauses.map(c => c.id);
      this.$store.dispatch(`problem/${ADD_CAUSE}`, { id: this.problemId, ishikawaBranch: this.branch.id, type: 2 })
        .then(() => {
          this.$nextTick(() => {
            preventPastingStyles();
            this.branch.wCauses.forEach((cause) => {
              if (!oldIds.includes(cause.id)) {
                // Focus the text field of the new cause
                const [comp] = this.$refs[`cause${cause.id}`];
                if (comp) {
                  comp.startEdit();
                }
              }
            });
          });
          this.sendSocketMessage({ socketCompany: 1, id: this.problemId, msg: SocketMessages.CC_MSGS.PROBLEM_SHEET });
        })
        .catch(this.handleError);
    },
    /**
     * Handle edit events
     * @param  {Object} params - Params to send through XHR body (id and problemId will be overridden)
     * @param  {Number} causeId - If the cause to edit isn't this.cause, specify the causeId
     */
    editCause(editParams, causeId) {
      causeId = causeId || this.cause.id;
      const params = { id: causeId, problemId: this.problemId, ...editParams };
      this.$store.dispatch(`problem/${EDIT_CAUSE}`, params)
        .then(() => {
          this.sendSocketMessage({ socketCompany: 1, id: this.problemId, msg: SocketMessages.CC_MSGS.PROBLEM_SHEET });
        })
        .catch(this.handleError);
    },
    /** Handle XHR errors */
    handleError(msg) {
      if (msg === -2) {
        calert(this.$t('message.PB_NO_ACCESS'), [{ title: this.$t('message.OK') }]);
      } else {
        calert(this.$t('message.PB_EDITION_ERROR'), [{ title: this.$t('message.OK') }]);
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.ishikawa-branch {
  text-align: right;
  display: inline-flex;
  position: relative;

  > canvas {
    position: absolute;
    top: 0px;
    left: 0px;
    width: 100%;
    height: 100%;
  }

  &__content-wrapper {
    position: relative;
    padding: 7px 0px;
    box-sizing: border-box;
    // Size when branch is empty
    min-width: 280px;

    button.add-button {
      display: inline-flex;
      padding: 5px;
      margin-right: 15px;

      &:hover {
        background: rgba(0, 78, 168, 0.2);
      }
      &.ui-droppable-hover {
        background: rgba(164, 183, 28, 0.5);
      }
    }
  }

  .branch-head {
    display: inline-block;
    padding: 0px 20px;
    border: 1px solid $color-shizen-light-blue;
    background: $color-shizen-light-blue-hover;
    border-radius: $high-border-radius;
    display: inline-flex;
    align-items: center;
    box-sizing: border-box;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;


    &.ui-droppable-hover {
      background: $color-shizen-blue-btn-shadow-color;
    }
  }
}
</style>
